import React, { useCallback, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import { IBigscreenSettings } from "../../../../constants/interfaces";
import {sortAlphabetically} from "../../helper/formatters";
import useEventFields from "../../../../hooks/useEventFields";
import useCustomFields from "../../../../hooks/useCustomFields";

const sorts = [
    {
        "value": "pubdate",
        "label": "Publish date"
    },
    {
        "value": "webPageviews",
        "label": "Web page views"
    },
    {
        "value": "appPageviews",
        "label": "App page views"
    },
    {
        "value": "totalPageviews",
        "label": "Total page views"
    },
    {
        "value": "conversion",
        "label": "Conversion"
    },
    {
        "value": "averageReadingTime",
        "label": "Avg. reading time"
    },
    {
        "value": "totalReadingTime",
        "label": "Total reading time"
    },
    {
        "value": "averageReadDepth",
        "label": "Avg. read depth"
    },
    {
        "value": "averageScrollDepth",
        "label": "Avg. scroll depth"
    },
    {
        "value": "facebookConsumptions",
        "label": "Facebook consumptions"
    },
    {
        "value": "facebookLikes",
        "label": "Facebook likes"
    },
    {
        "value": "facebookComments",
        "label": "Facebook comments"
    },
    {
        "value": "facebookShares",
        "label": "Facebook shares"
    },
    {
        "value": "facebookLinkClicks",
        "label": "Facebook link clicks"
    },
    {
        "value": "facebookSocialScore",
        "label": "Facebook social score"
    },
    {
        "value": "socialActions",
        "label": "Social actions"
    },
    {
        "value": "twitterLikes",
        "label": "Twitter likes"
    },
    {
        "value": "twitterReply",
        "label": "Twitter replies"
    },
    {
        "value": "twitterScore",
        "label": "Twitter score"
    },
    // TODO: replace active users with total visitors
    // {
    //     "value": "totalVisitors",
    //     "label": "Total visitors"
    // },
    {
        "value": "webVisitors",
        "label": "Web visitors"
    },
    {
        "value": "appVisitors",
        "label": "App visitors"
    },
];

const videoSorts = [
	{
		"value": "pubdate",
		"label": "Publish date"
	},
	{
		"value": "total",
		"label": "Total video views"
	},
	{
		"value": "totalWeb",
		"label": "Total web video views"
	},
	{
		"value": "totalApp",
		"label": "Total app video views"
	}
];

type Props = {
    updateConfig: Function,
    value: string,
    isLoadingBigscreenSettings: boolean,
    bigscreenSettings?: IBigscreenSettings[],
    isVideo?: boolean
}

type Option = {
    value: string,
    label: string
}

const Sort = ({ value, isLoadingBigscreenSettings, bigscreenSettings, isVideo, updateConfig}: Props) => {
    const eventFields = useEventFields(bigscreenSettings);
    const customFields = useCustomFields(bigscreenSettings);

    // @ts-ignore
    const defaultColumn: Option = isVideo
        ? videoSorts.find(col => col.value === 'total')
        : sorts.find(col => col.value === 'totalPageviews');

    const sortedOptions = useMemo(() => {
        const newSorts = isVideo ? [...videoSorts] : [...sorts];

        if (bigscreenSettings) {
            if (eventFields) {
                eventFields.forEach(eventField => {
                    newSorts.push({
                        value: eventField.name,
                        label: eventField.label
                    });
                });
            }

            if (customFields) {
                customFields.forEach(metric => {
                    newSorts.push({
                        value: metric.id,
                        label: metric.label
                    });
                });
            }
        }

        return sortAlphabetically(newSorts, 'label');
    }, [isVideo, bigscreenSettings, eventFields, customFields]);
    
	const getSortColumn = useCallback((column: string) => {
        return sortedOptions.find((sort: { value: string, label: string }) => sort.value === column);
    }, [sortedOptions]);

    const sortValue = useMemo(() => {
        if (!value) return defaultColumn;
    
        const s = getSortColumn(value.split(".")[0]);
        return s || defaultColumn;
    }, [value, getSortColumn]);


    useEffect(() => {
        if (!value) {
            updateConfig(["sort"], [defaultColumn.value + ".desc"]);
            return;
        }
    
        const sortVal = getSortColumn(value.split(".")[0]);
        if (!sortVal) {
            updateConfig(["sort"], [defaultColumn.value + "." + value.split(".")[1]]);
        }
    }, [value, getSortColumn, updateConfig, defaultColumn]);

	const orders = [{label: "Ascending", value: "asc"}, {label: "Descending", value: "desc"}];
	const getOrderValue = () => {
	    if (value) {
            return orders.find((order: Option) => order.value === value.split(".")[1])
        }

	    return null;
    }

    const onSortChangeHandler = (e: any) => {
        updateConfig(["sort"], [e!.value + '.' + value.split(".")[1]]);
    };

    return (
        <div className="config-item__selects">
            <div className="form-group">
                <label htmlFor="sortBy1">Sort list on:</label>
                <Select
                    id="sortBy1"
                    className="basic-single"
                    classNamePrefix="so-select-sort"
                    isSearchable={true}
                    isLoading={isLoadingBigscreenSettings}
                    value={sortValue}
                    options={sortedOptions}
                    onChange={onSortChangeHandler}
                    menuPortalTarget={document.body}
                    styles={{menuPortal: base => ({...base, zIndex: 9999})}}
                />
            </div>

            <div className="form-group">
                <label htmlFor="sortBy2">Order:</label>
                <Select
                    id="sortBy2"
                    className="basic-single"
                    classNamePrefix="so-select-sort"
                    isSearchable={true}
                    value={getOrderValue()}
                    options={orders}
                    onChange={e => updateConfig(["sort"], [value.split(".")[0] + '.' + e!.value])}
                    menuPortalTarget={document.body}
                    styles={{menuPortal: base => ({...base, zIndex: 9999})}}
                />
            </div>
        </div>
    );
};

Sort.defaultProps = {
    isLoadingBigscreenSettings: {}
};

const mapStateToProps = (state: any) => {
    return {
        isLoadingBigscreenSettings: state.bigscreenSettings ? state.bigscreenSettings.isLoading : {}
    }
};

export default connect(mapStateToProps)(Sort);